import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { applyPalette } from "$/modules/shared/lib/apply-palette";
import {
	CUSTOM_PALETTE_ID,
	getPalettePreset,
} from "$/modules/shared/lib/palettes";
import { composeTheme, useAppearance } from "$/modules/shared/hooks/useAppearance";

/**
 * Bridges the appearance store (design system + mode + palette) to the DOM:
 * - resolves "system" mode via prefers-color-scheme and calls next-themes'
 *   setTheme with the composed `${designSystem}-${mode}` class.
 * - injects the active color palette (preset or custom) as CSS custom
 *   properties, overriding the design system's seed colors at runtime.
 */
export const AppearanceSync = () => {
	const { designSystem, mode, paletteId, customPrimary, customSecondary } =
		useAppearance();
	const { setTheme } = useTheme();
	const [systemDark, setSystemDark] = useState(false);

	useEffect(() => {
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		setSystemDark(media.matches);
		const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
		media.addEventListener("change", onChange);
		return () => media.removeEventListener("change", onChange);
	}, []);

	useEffect(() => {
		const resolvedMode =
			mode === "system" ? (systemDark ? "dark" : "light") : mode;
		setTheme(composeTheme(designSystem, resolvedMode));
	}, [designSystem, mode, systemDark, setTheme]);

	useEffect(() => {
		if (paletteId === CUSTOM_PALETTE_ID) {
			applyPalette(customPrimary, customSecondary);
			return;
		}
		const preset = getPalettePreset(paletteId);
		if (preset) applyPalette(preset.primary, preset.secondary);
	}, [paletteId, customPrimary, customSecondary]);

	return null;
};
